// ===========================================
// View Student Dialog Component
// ===========================================

import { getStudentAgeGroup, getStudentFamily } from '../../../api/users.api';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Autorenew as MakeupIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  EmojiEvents as TrophyIcon,
  Event as EventIcon,
  Repeat as EmailSequenceIcon,
  EventBusy as AbsenceIcon,
  Folder as FolderIcon,
  LocalOffer as LocalOfferIcon,
  MusicNote as MusicNoteIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Student } from '../../../api/users.api';
import { useStudentEnrollments } from '../../../hooks/useUsers';
import { useNotesByStudent } from '../../../hooks/useNotes';
import { useGoogleDriveFiles } from '../../../hooks/useGoogleDrive';
import {
  useStudentAchievements,
  useAchievementDefinitions,
  useAwardAchievement,
  useAwardSpecialAchievement,
  useRevokeAchievement,
} from '../../../hooks/useGamification';
import { useCustomDiscounts, useVariableDiscountSettings } from '../../../hooks/useDiscounts';
import { useDiscountCoverageLabel } from '../../../hooks/useDiscountCoverageLabel';
import { coverageFromSchoolDefault } from '../../../utils/discountCoverage';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAdminStudentCredits, useAdminStudentCreditLogs, useAdminMakeupEligibilities, useAdminGrantCredit } from '../../../hooks/useBooking';
import { useTerms } from '../../../hooks/useAdmin';
import type { Achievement, AwardAchievementData, AwardSpecialAchievementData } from '../../../types/gamification.types';
import { AchievementGrid, AwardAchievementModal } from '../../gamification';
import ConfirmDialog from '../../common/ConfirmDialog';
import { DEFAULT_AGE_GROUP_COLOR, PREVIEW_ITEM_COUNT } from './students.constants';
import { getStudentStatusColor, getStudentStatusLabel } from './students.helpers';
import EnrollmentItem from './EnrollmentItem';
import StudentBookingsList from './StudentBookingsList';
import StartEmailSequenceDialog from './StartEmailSequenceDialog';
import StudentEmailSequencesList from './StudentEmailSequencesList';
import { PermissionGate } from '../../common/PermissionGate';
import NoteItem from './NoteItem';
import FileVisibilityChip from './FileVisibilityChip';
import AllNotesDialog from './AllNotesDialog';
import ReportAbsenceDialog from './ReportAbsenceDialog';
import { MediaExposureDot } from '../../parent/MediaExposureSelector';

interface Props {
  open: boolean;
  student: Student | null;
  onClose: () => void;
  onEdit: () => void;
}

/**
 * Dialog showing student profile details (read-only view)
 */
export default function ViewStudentDialog({ open, student, onClose, onEdit }: Props) {
  const [allNotesOpen, setAllNotesOpen] = useState(false);
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [achievementToRevoke, setAchievementToRevoke] = useState<Achievement | null>(null);
  const [reportAbsenceOpen, setReportAbsenceOpen] = useState(false);
  const [startSequenceOpen, setStartSequenceOpen] = useState(false);
  const [enrollmentTermFilter, setEnrollmentTermFilter] = useState<'current' | 'all'>('current');
  const { enqueueSnackbar } = useSnackbar();

  // Fetch data for the viewed student
  const { data: enrollments, isLoading: isLoadingEnrollments } = useStudentEnrollments(
    student?.id || null,
    enrollmentTermFilter
  );
  const { data: notes, isLoading: isLoadingNotes } = useNotesByStudent(student?.id || '');
  const { data: files, isLoading: isLoadingFiles } = useGoogleDriveFiles(
    student ? { studentId: student.id } : undefined,
    { enabled: !!student }
  );

  // Achievement data
  const { data: studentAchievements, isLoading: isLoadingAchievements } = useStudentAchievements(
    student?.id || ''
  );
  const { data: achievementDefinitions } = useAchievementDefinitions();
  const awardMutation = useAwardAchievement();
  const awardSpecialMutation = useAwardSpecialAchievement();
  const revokeMutation = useRevokeAchievement();

  // Discount data: pricing, so only with operations_pricing read (whole-slice /qa F-L1). Without it
  // both stay undefined and the Discounts section does not render.
  const mayReadDiscounts = usePermissions().can('operations_pricing', 'read');
  const { data: discountsData } = useCustomDiscounts({ skip: 0, take: 100 }, { enabled: mayReadDiscounts });
  const { data: variableSettings } = useVariableDiscountSettings({ enabled: mayReadDiscounts });
  // #625 D9 (/qa #625 L3): every discount named here says what it covers.
  const coverageOf = useDiscountCoverageLabel(mayReadDiscounts);
  // The automatic discounts (family, instrument, program level) cover the school default (D6;
  // pre-merge /qa L5), read with the same gate.
  const automaticCovers = mayReadDiscounts && variableSettings ? coverageOf(coverageFromSchoolDefault(variableSettings)) : null;

  // Makeup credit data
  const { data: terms } = useTerms();
  const currentTermId = (() => {
    const now = new Date();
    return terms?.find((t) => new Date(t.startDate) <= now && new Date(t.endDate) >= now && t.isActive)?.id || '';
  })();
  const currentTermName = terms?.find((t) => t.id === currentTermId)?.name;
  const { data: makeupCredits, isLoading: isLoadingCredits } = useAdminStudentCredits(
    student?.id || '',
    currentTermId
  );
  const { data: creditLogs, isLoading: isLoadingCreditLogs } = useAdminStudentCreditLogs(
    student?.id || ''
  );
  const { data: eligibilities } = useAdminMakeupEligibilities({ studentId: student?.id });

  // Send / Add more make-up credit modal state (#574). SEND gives the family a Book button on their
  // make-up page; ADD MORE raises the balance only, for the office to use from Book Extra Class.
  const [offerCreditModal, setOfferCreditModal] = useState<{
    open: boolean;
    mode: 'send' | 'add';
    additionalCredits: number;
    reason: string;
  }>({ open: false, mode: 'add', additionalCredits: 1, reason: '' });
  const grantCredit = useAdminGrantCredit();

  const handleOpenOfferCreditModal = (mode: 'send' | 'add') => {
    grantCredit.reset(); // a previous failure must not greet the next open
    setOfferCreditModal({ open: true, mode, additionalCredits: 1, reason: '' });
  };
  const handleCloseOfferCreditModal = () =>
    setOfferCreditModal({ open: false, mode: 'add', additionalCredits: 1, reason: '' });
  const handleOfferCredit = () => {
    if (!currentTermId || offerCreditModal.reason.trim().length < 3 || !student) return;
    grantCredit.mutate(
      {
        studentId: student.id,
        termId: currentTermId,
        additionalCredits: offerCreditModal.additionalCredits,
        reason: offerCreditModal.reason.trim(), // as the other dialog does: no stray spaces in the audit row
        // Each action has ONE fixed meaning: the family gets a Book button only when it is SENT.
        offerToFamily: offerCreditModal.mode === 'send',
      },
      { onSuccess: handleCloseOfferCreditModal }
    );
  };

  const handleOpenAwardModal = () => {
    awardMutation.reset();
    awardSpecialMutation.reset();
    setAwardModalOpen(true);
  };

  const handleAwardExisting = async (data: AwardAchievementData) => {
    if (!student) return;
    try {
      await awardMutation.mutateAsync({ studentId: student.id, data });
      enqueueSnackbar(`Achievement awarded to ${student.firstName}!`, { variant: 'success' });
    } catch {
      // Error displayed in modal via error prop
    }
  };

  const handleAwardSpecial = async (data: AwardSpecialAchievementData) => {
    if (!student) return;
    try {
      await awardSpecialMutation.mutateAsync({ studentId: student.id, data });
      enqueueSnackbar(`Special achievement awarded to ${student.firstName}!`, { variant: 'success' });
    } catch {
      // Error displayed in modal via error prop
    }
  };

  const handleRevokeClick = (achievement: Achievement) => {
    setAchievementToRevoke(achievement);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!achievementToRevoke) return;
    try {
      await revokeMutation.mutateAsync(achievementToRevoke.id);
      enqueueSnackbar('Achievement revoked', { variant: 'info' });
      setRevokeDialogOpen(false);
      setAchievementToRevoke(null);
    } catch {
      enqueueSnackbar('Failed to revoke achievement', { variant: 'error' });
    }
  };

  if (!student) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            // ⚠️ Without this the action group overflows at ~400px: the row is a
            // flex container with no wrap, so a fourth control pushes the title
            // off-screen rather than moving to a second line.
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {student.firstName} {student.lastName}
            </Typography>
            <MediaExposureDot
              exposure={(getStudentFamily(student)?.mediaExposure ?? 'PENDING') as 'NO' | 'BLUR' | 'FULL' | 'PENDING'}
            />
            {getStudentAgeGroup(student) && (
              <Chip
                label={getStudentAgeGroup(student)!.name}
                size="small"
                sx={{
                  bgcolor: getStudentAgeGroup(student)!.color || DEFAULT_AGE_GROUP_COLOR,
                  color: '#fff',
                }}
              />
            )}
            <Chip
              label={getStudentStatusLabel(student.status as 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE')}
              size="small"
              color={getStudentStatusColor(student.status as 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE')}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<AbsenceIcon />}
              onClick={() => setReportAbsenceOpen(true)}
            >
              Report Absence
            </Button>
            {/*
              An IconButton, not a second labelled button: the title row already
              carries a wide "Report Absence" and a fourth labelled control
              overflows on a phone even with the wrap above.
            */}
            <PermissionGate resource="communications_emails" action="write">
              <Tooltip title="Start email series">
                <IconButton size="small" onClick={() => setStartSequenceOpen(true)}>
                  <EmailSequenceIcon />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <Tooltip title="Edit Student">
              <IconButton size="small" onClick={onEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Basic Information
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography>
                      {student.firstName} {student.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Birth Date
                    </Typography>
                    <Typography>
                      {student.birthDate
                        ? format(new Date(student.birthDate), 'd MMMM yyyy')
                        : 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Family
                    </Typography>
                    <Typography>{getStudentFamily(student)?.name || 'No family assigned'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Age Group
                    </Typography>
                    <Typography>{getStudentAgeGroup(student)?.name || 'Not determined'}</Typography>
                  </Grid>
                  {student.email && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography>
                        <Link
                          href={`mailto:${student.email}`}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <EmailIcon sx={{ fontSize: 16 }} />
                          {student.email}
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                  {student.phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography>
                        <Link
                          href={`tel:${student.phone}`}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <PhoneIcon sx={{ fontSize: 16 }} />
                          {student.phone}
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>

            {/* Bookings */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                <EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Bookings
              </Typography>
              <StudentBookingsList studentId={student.id} />
            </Box>

            {/* Email series */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                <EmailSequenceIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Email series
              </Typography>
              <StudentEmailSequencesList studentId={student.id} />
            </Box>

            {/* Makeup Credits */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  <MakeupIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Makeup Credits
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenOfferCreditModal('send')} disabled={!currentTermId}>
                    Send make-up credit
                  </Button>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenOfferCreditModal('add')} disabled={!currentTermId}>
                    Add more make-up credit
                  </Button>
                </Box>
              </Box>

              {/* Credit summary for current term */}
              {currentTermId && (
                <Box sx={{ mb: 2 }}>
                  {isLoadingCredits ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : makeupCredits ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Current term: {currentTermName}
                      </Typography>
                      <Grid container spacing={1.5} sx={{ mb: 1 }}>
                        <Grid item xs={3}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="h5" color="primary">
                              {makeupCredits.creditsGranted ?? 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="h5" color="success.main">
                              {makeupCredits.creditsRemaining ?? 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Available
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="h5" color="text.secondary">
                              {makeupCredits.creditsUsed ?? 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Used by office
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="h5" color="warning.main">
                              {makeupCredits.creditsRedeemed ?? 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Booked by family
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No credits for current term.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Credit grant log */}
              {isLoadingCreditLogs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : creditLogs && creditLogs.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Credit history ({creditLogs.length})
                  </Typography>
                  <Stack spacing={1}>
                    {creditLogs.map((log) => (
                      <Paper key={log.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {Math.abs(log.creditsAdded)} credit{Math.abs(log.creditsAdded) !== 1 ? 's' : ''}{' '}
                            {log.creditsAdded < 0 ? 'used' : 'granted'}
                          </Typography>
                          <Chip label={log.Term.name} size="small" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {log.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(log.createdAt), 'd MMM yyyy')} — by {log.GrantedBy.firstName} {log.GrantedBy.lastName}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {/* Eligibility history (absence-based) */}
              {eligibilities && eligibilities.length > 0 && (
                <Box sx={{ mt: creditLogs && creditLogs.length > 0 ? 2 : 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Absence eligibilities ({eligibilities.length})
                  </Typography>
                  <Stack spacing={1}>
                    {eligibilities.map((elig) => (
                      <Paper key={elig.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Excused absence — {elig.Instrument?.name}
                          </Typography>
                          <Chip
                            label={elig.redeemedAt ? 'Used' : 'Available'}
                            size="small"
                            color={elig.redeemedAt ? 'success' : 'warning'}
                          />
                        </Box>
                        {elig.Attendance?.absenceReason && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {elig.Attendance.absenceReason}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {elig.Attendance?.date ? format(new Date(elig.Attendance.date), 'd MMM yyyy') : ''}
                          {' — '}
                          {elig.Lesson?.name}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Empty state — only show if data has actually loaded (not just query disabled due to no active term) */}
              {!isLoadingCredits && !isLoadingCreditLogs && !!terms &&
                (!currentTermId || !makeupCredits || makeupCredits.creditsGranted === 0) &&
                (!creditLogs || creditLogs.length === 0) &&
                (!eligibilities || eligibilities.length === 0) && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography color="text.secondary">No makeup credits on record</Typography>
                </Paper>
              )}
            </Box>

            {/* Discounts Section */}
            {(() => {
              // Get custom discounts
              const applicableDiscounts = discountsData?.discounts.filter(
                d => d.scope === 'STUDENT' && d.applicableStudents.includes(student.id)
              ) || [];

              // Calculate variable discount eligibility
              const uniqueInstruments = new Set(
                enrollments?.map(e => e.lesson?.instrument).filter(Boolean) || []
              );

              const instrumentEligible = variableSettings?.instrumentDiscountEnabled &&
                uniqueInstruments.size >= variableSettings.instrumentMinCount;

              // Only show section if there are custom discounts OR variable discounts
              if (applicableDiscounts.length === 0 && !instrumentEligible) {
                return null;
              }

              return (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                    <LocalOfferIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    Discounts
                  </Typography>
                  <Stack spacing={2}>
                    {/* Custom Discounts */}
                    {applicableDiscounts.length > 0 && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          Custom Discounts
                        </Typography>
                        <Stack spacing={1}>
                          {applicableDiscounts.map((discount) => (
                            <Paper key={discount.id} variant="outlined" sx={{ p: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Discount Name
                                  </Typography>
                                  <Typography fontWeight="medium">{discount.name}</Typography>
                                  {coverageOf(discount) && (
                                    <Typography variant="caption" color="text.secondary" display="block" data-testid="student-discount-detail-covers">
                                      Covers {coverageOf(discount)}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Value
                                  </Typography>
                                  <Typography>
                                    {discount.isPercent ? `${discount.value}%` : `$${Number(discount.value).toFixed(2)}`}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Status
                                  </Typography>
                                  <Box>
                                    <Chip
                                      label={discount.status}
                                      size="small"
                                      color={discount.status === 'ACTIVE' ? 'success' : discount.status === 'INACTIVE' ? 'default' : 'warning'}
                                    />
                                  </Box>
                                </Grid>
                                {discount.description && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                      Description
                                    </Typography>
                                    <Typography variant="body2">{discount.description}</Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Variable Discounts */}
                    {instrumentEligible && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          Variable Discounts
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
                          <Stack spacing={2}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    Instrument Discount ({variableSettings?.instrumentDiscountPercent}%)
                                  </Typography>
                                  {automaticCovers && (
                                    <Typography variant="caption" color="text.secondary" display="block" data-testid="student-automatic-covers">
                                      Covers {automaticCovers}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {uniqueInstruments.size} unique instruments: {Array.from(uniqueInstruments).join(', ')}
                                  </Typography>
                                </Box>
                                <Chip label="Eligible ✓" size="small" color="success" />
                              </Box>
                            </Box>
                          </Stack>
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })()}

            {/* Admin Notes (internal notes field) */}
            {student.notes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                  <NotesIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Admin Notes
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'warning.light',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="body2" color="warning.dark">
                    {student.notes}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Class Enrollments */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  <MusicNoteIcon
                    sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }}
                  />
                  Class Enrollments ({enrollments?.length || 0})
                </Typography>
                <ToggleButtonGroup
                  value={enrollmentTermFilter}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setEnrollmentTermFilter(v)}
                >
                  <ToggleButton value="current" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
                    Current & Future
                  </ToggleButton>
                  <ToggleButton value="all" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
                    All Terms
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {isLoadingEnrollments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : enrollments && enrollments.length > 0 ? (
                <Stack spacing={1}>
                  {enrollments.map((enrollment) => (
                    <EnrollmentItem
                      key={enrollment.id}
                      enrollment={enrollment}
                      showRemoveButton={false}
                    />
                  ))}
                </Stack>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    {enrollmentTermFilter === 'current'
                      ? 'Not enrolled in any current or upcoming classes'
                      : 'Not enrolled in any classes'}
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* Achievements */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  <TrophyIcon
                    sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: 'warning.main' }}
                  />
                  Achievements ({studentAchievements?.length || 0})
                </Typography>
                <Button
                  size="small"
                  startIcon={<TrophyIcon />}
                  onClick={handleOpenAwardModal}
                >
                  Award
                </Button>
              </Box>
              <AchievementGrid
                achievements={studentAchievements || []}
                definitions={achievementDefinitions || []}
                showUnearned
                size="small"
                isLoading={isLoadingAchievements}
                emptyMessage="No achievements yet"
                onAchievementClick={(item) => {
                  if ('earnedAt' in item) {
                    handleRevokeClick(item as Achievement);
                  }
                }}
              />
            </Box>

            {/* Recent Notes */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  <NotesIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Teacher Notes ({notes?.length || 0})
                </Typography>
                {notes && notes.length > PREVIEW_ITEM_COUNT && (
                  <Button size="small" onClick={() => setAllNotesOpen(true)}>
                    View All ({notes.length})
                  </Button>
                )}
              </Box>
              {isLoadingNotes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notes && notes.length > 0 ? (
                <>
                  {notes.slice(0, PREVIEW_ITEM_COUNT).map((note) => (
                    <NoteItem key={note.id} note={note} />
                  ))}
                  {notes.length > PREVIEW_ITEM_COUNT && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => setAllNotesOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      View All Notes ({notes.length})
                    </Button>
                  )}
                </>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography color="text.secondary">No teacher notes yet</Typography>
                </Paper>
              )}
            </Box>

            {/* Files */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                <FolderIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Files ({files?.length || 0})
              </Typography>
              {isLoadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : files && files.length > 0 ? (
                <Stack spacing={1}>
                  {files.slice(0, PREVIEW_ITEM_COUNT).map((file) => (
                    <Paper key={file.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {file.fileName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(file.createdAt), 'd MMM yyyy')}
                              </Typography>
                              <FileVisibilityChip visibility={file.visibility} />
                            </Box>
                          </Box>
                        </Box>
                        {file.webViewLink && (
                          <Button
                            size="small"
                            variant="outlined"
                            href={file.webViewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  ))}
                  {files.length > PREVIEW_ITEM_COUNT && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mt: 1 }}
                    >
                      And {files.length - PREVIEW_ITEM_COUNT} more files...
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography color="text.secondary">No files uploaded yet</Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={onEdit}>
            Edit Student
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Notes Modal */}
      <AllNotesDialog
        open={allNotesOpen}
        studentName={`${student.firstName} ${student.lastName}`}
        notes={notes || []}
        onClose={() => setAllNotesOpen(false)}
      />

      {/* Award Achievement Modal */}
      <AwardAchievementModal
        open={awardModalOpen}
        onClose={() => setAwardModalOpen(false)}
        studentName={`${student.firstName} ${student.lastName}`}
        definitions={achievementDefinitions || []}
        onAwardExisting={handleAwardExisting}
        onAwardSpecial={handleAwardSpecial}
        isAwarding={awardMutation.isPending || awardSpecialMutation.isPending}
        error={awardMutation.error?.message || awardSpecialMutation.error?.message || null}
      />

      {/* Revoke Achievement Confirmation */}
      <ConfirmDialog
        open={revokeDialogOpen}
        title="Revoke Achievement"
        message={`Are you sure you want to revoke "${achievementToRevoke?.name}" from ${student.firstName}?`}
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revokeMutation.isPending}
        onConfirm={handleRevokeConfirm}
        onCancel={() => {
          setRevokeDialogOpen(false);
          setAchievementToRevoke(null);
        }}
      />

      {/* Report Absence Modal */}
      <ReportAbsenceDialog
        open={reportAbsenceOpen}
        studentId={student.id}
        studentName={`${student.firstName} ${student.lastName}`}
        onClose={() => setReportAbsenceOpen(false)}
      />

      <StartEmailSequenceDialog
        open={startSequenceOpen}
        studentId={student.id}
        studentName={`${student.firstName} ${student.lastName}`}
        onClose={() => setStartSequenceOpen(false)}
      />

      {/* Send / Add more make-up credit modal (#574): one dialog, two fixed meanings */}
      <Dialog open={offerCreditModal.open} onClose={handleCloseOfferCreditModal}>
        <DialogTitle>
          {offerCreditModal.mode === 'send' ? 'Send make-up credit' : 'Add more make-up credit'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {offerCreditModal.mode === 'send'
              ? `Send make-up credit to ${student.firstName} ${student.lastName}'s family. They get a Book button on their make-up page and book it themselves.`
              : `Add more make-up credit for ${student.firstName} ${student.lastName}. It is for you to use when you book a make-up from Book Extra Class; the family sees nothing.`}
          </Typography>
          <TextField
            fullWidth
            label="Number of Credits"
            type="number"
            value={offerCreditModal.additionalCredits}
            // Clamped to what the server accepts (1 to 10): typing 15 used to leave the button enabled
            // and the server answered 400 with nothing shown (#574 QA).
            onChange={(e) =>
              setOfferCreditModal({
                ...offerCreditModal,
                additionalCredits: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)),
              })
            }
            inputProps={{ min: 1, max: 10 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Reason"
            placeholder="e.g. Class cancelled, Absent due to school camp"
            value={offerCreditModal.reason}
            onChange={(e) => setOfferCreditModal({ ...offerCreditModal, reason: e.target.value })}
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
            error={offerCreditModal.reason.length > 0 && offerCreditModal.reason.trim().length < 3}
            helperText={`Minimum 3 characters (${offerCreditModal.reason.length}/500)`}
          />
          {grantCredit.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {grantCredit.error instanceof Error
                ? grantCredit.error.message
                : 'The credit could not be added. Please try again.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOfferCreditModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleOfferCredit}
            disabled={offerCreditModal.reason.trim().length < 3 || grantCredit.isPending}
          >
            {grantCredit.isPending ? (
              <CircularProgress size={20} />
            ) : offerCreditModal.mode === 'send' ? (
              'Send credit'
            ) : (
              'Add credit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
